import { ActionButton } from '@/components/common/ActionButton';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { PageHeader } from '@/components/common/PageHeader';
import { Reveal } from '@/components/common/Reveal';
import { TableSkeleton } from '@/components/common/Skeleton';
import { MOTION } from '@/components/common/motion';
import { CARD } from '@/components/common/surfaces';
import { ConfirmDeleteDialog } from '@/components/dialogs/ConfirmDeleteDialog';
import { CreateProjectCard } from '@/components/projects/CreateProjectCard';
import { FilterControls } from '@/components/projects/FilterControls';
import { NewProjectRuleSet } from '@/components/projects/NewProjectRuleSet';
import { ProjectGrid } from '@/components/projects/ProjectGrid';
import { ProjectsTable } from '@/components/projects/ProjectsTable';
import { SortControls } from '@/components/projects/SortControls';
import { UploadQueuePanel } from '@/components/projects/UploadQueuePanel';
import { useToolSystem } from '@/context/ToolSystemContext';
import { useUploadQueueContext } from '@/context/UploadQueueContext';
import { useNewProjectRuleSet } from '@/hooks/useNewProjectRuleSet';
import { useProjects } from '@/hooks/useProjects';
import type { SortConfig } from '@/types/project.types';
import type { ToolSystem } from '@/types/toolSystem.types';
import { TABLE_FOOTER } from '@/utils/stickyColumns';
import { CaretLeft, CaretRight, ListChecks, SquaresFour, Table, Trash } from '@phosphor-icons/react';
import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

/** The rules app ships with this one, as a static page under `public/`. */
const RULES_APP_PATH = '/rules-app/';

/**
 * The rules app is a separate document, so it inherits nothing from here — not this language,
 * not the stored choice behind it. The link therefore hands the language over explicitly;
 * without it the app falls back to the browser and then to English, the same way this one does.
 */
function rulesAppUrl(language: string, toolSystem: ToolSystem): string {
  const url = new URL(RULES_APP_PATH, window.location.origin);
  url.searchParams.set('lang', language);
  url.searchParams.set('tool_system', toolSystem);
  return url.toString();
}

/**
 * The link out to the rules app.
 *
 * An anchor rather than an `ActionButton`, because it navigates — it borrows the secondary
 * button's recipe the same way the Collisions link does.
 */
const LINK_BUTTON_CLASSES = `inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:border-gray-300 hover:bg-gray-50 active:scale-[0.98] ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2`;

type ViewMode = 'grid' | 'table';

/** The segmented control's track: one recessed strip the active segment sits raised inside. */
const SEGMENT_TRACK = 'inline-flex gap-1 rounded-lg bg-gray-100 p-1';

/** A segment. `rounded` is the 4 px step inside the track's 8 px at 4 px of padding. */
const SEGMENT_BASE = `rounded px-3 py-1 text-sm font-medium active:scale-[0.98] ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40`;

const SEGMENT_ACTIVE = `${SEGMENT_BASE} bg-white text-gray-900 shadow-sm`;

const SEGMENT_IDLE = `${SEGMENT_BASE} text-gray-500 hover:text-gray-900`;

export const Home: FC = () => {
  const { t, i18n } = useTranslation();
  const { toolSystem } = useToolSystem();
  const {
    projects,
    isLoading,
    error,
    bulkDeleteProjects,
    refetch,
    page,
    pageSize,
    setPage,
    hasNextPage,
    hasPrevPage,
    totalPages,
    totalCount,
    sort,
    order,
    setSort,
    ownerFilter,
    setOwnerFilter,
  } = useProjects();
  const { uploads, startUpload, cancelUpload } = useUploadQueueContext();
  // Settled before the drop: a project's rule set is fixed once its model is processed.
  const {
    ruleSets: newProjectRuleSets,
    name: newProjectRuleSet,
    choose: chooseNewProjectRuleSet,
    isLoading: isLoadingRuleSets,
    error: ruleSetsError,
  } = useNewProjectRuleSet();
  const navigate = useNavigate();

  // Server-side sort exposed to the sort/table controls as a SortConfig.
  const sortConfig: SortConfig = { field: sort, order };
  const handleSortChange = (config: SortConfig) => setSort(config.field, config.order);

  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeletingProjects, setIsDeletingProjects] = useState(false);

  const handleProjectClick = (projectId: string) => {
    navigate(`/project/${projectId}`);
  };

  const handleUserClick = (ownerId: string) => {
    // If the clicked user is already filtered, switch back to "All users".
    setOwnerFilter(ownerFilter === ownerId ? null : ownerId);
  };

  const handleProjectSelect = (projectId: string, selected: boolean) => {
    setSelectedProjectIds((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(projectId);
      } else {
        newSet.delete(projectId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedProjectIds(new Set(projects.map((p) => p.id)));
    } else {
      setSelectedProjectIds(new Set());
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedProjectIds.size === 0) return;

    setIsDeletingProjects(true);
    try {
      await bulkDeleteProjects(Array.from(selectedProjectIds));
      setSelectedProjectIds(new Set());
      setShowDeleteDialog(false);
    } catch (_err) {
      // Error handling is done in bulkDeleteProjects
    } finally {
      setIsDeletingProjects(false);
    }
  };

  const viewModes: { mode: ViewMode; label: string; icon: ReactNode }[] = [
    { mode: 'grid', label: t('home.gridView'), icon: <SquaresFour size={16} aria-hidden="true" /> },
    { mode: 'table', label: t('home.tableView'), icon: <Table size={16} aria-hidden="true" /> },
  ];

  // The count is the page's one live fact, so it goes under the title rather than into the toolbar.
  const subtitle = totalCount != null ? t('home.projectCount', { count: totalCount }) : undefined;

  const rulesAppAction = (
    <a
      href={rulesAppUrl(i18n.resolvedLanguage || i18n.language, toolSystem)}
      target="_blank"
      rel="noopener noreferrer"
      className={LINK_BUTTON_CLASSES}
      title={t('home.holeRules')}
    >
      <ListChecks size={16} aria-hidden="true" />
      {t('home.holeRules')}
    </a>
  );

  const deleteAction =
    selectedProjectIds.size > 0 ? (
      <ActionButton
        variant="danger"
        icon={<Trash size={16} aria-hidden="true" />}
        onClick={() => setShowDeleteDialog(true)}
      >
        {t('home.deleteSelected')}
        <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold tabular-nums">
          {selectedProjectIds.size}
        </span>
      </ActionButton>
    ) : null;

  return (
    <div className="flex flex-col gap-6">
      <Reveal>
        <PageHeader
          title={t('home.title')}
          subtitle={subtitle}
          actions={
            <>
              {deleteAction}
              {rulesAppAction}
            </>
          }
          titleId="projects-heading"
        />
      </Reveal>

      {error && <ErrorMessage message={error} />}

      <Reveal delayMs={80}>
        <section aria-labelledby="projects-heading" className="flex flex-col gap-6">
          {/* One row: how the list is shown on the left, how it is ordered and narrowed on the
              right. It wraps rather than scrolls, so nothing leaves the screen at 400 px. */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className={SEGMENT_TRACK} role="group" aria-label={t('home.viewModeLabel')}>
              {viewModes.map(({ mode, label, icon }) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setViewMode(mode)}
                  aria-pressed={viewMode === mode}
                  title={label}
                  aria-label={label}
                  className={viewMode === mode ? SEGMENT_ACTIVE : SEGMENT_IDLE}
                >
                  {icon}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-4">
              {/* Not a filter: it decides what the next upload is created with, and it sits here
                  because that has to be settled before the file is dropped. */}
              <NewProjectRuleSet
                ruleSets={newProjectRuleSets}
                value={newProjectRuleSet}
                onChange={chooseNewProjectRuleSet}
                isLoading={isLoadingRuleSets}
                error={ruleSetsError}
              />
              {/* The table sorts from its own headers, so the dropdown would be a second way to
                  say the same thing there. Filtering has nowhere else to live in either view. */}
              {viewMode === 'grid' && <SortControls sortConfig={sortConfig} onSortChange={handleSortChange} />}
              <FilterControls projects={projects} selectedOwnerId={ownerFilter} onOwnerFilterChange={setOwnerFilter} />
            </div>
          </div>

          {viewMode === 'grid' ? (
            <ProjectGrid
              projects={projects}
              isLoading={isLoading}
              onProjectClick={handleProjectClick}
              onUserClick={handleUserClick}
              selectedProjectIds={selectedProjectIds}
              onProjectSelect={handleProjectSelect}
              onStartUpload={(file) => startUpload(file, refetch, newProjectRuleSet)}
              holeRuleSet={newProjectRuleSet}
              ruleSets={newProjectRuleSets}
            />
          ) : (
            <div className="flex flex-col gap-6">
              <CreateProjectCard
                compact={true}
                onStartUpload={(file) => startUpload(file, refetch, newProjectRuleSet)}
                holeRuleSet={newProjectRuleSet}
              />
              {/* Without this the table's own "no projects" state flashes on every page change,
                  which reads as "there is nothing here" rather than "this is still arriving". */}
              {isLoading ? (
                <TableSkeleton rows={pageSize} columns={7} />
              ) : (
                <ProjectsTable
                  projects={projects}
                  selectedProjectIds={selectedProjectIds}
                  onProjectClick={handleProjectClick}
                  onProjectSelect={handleProjectSelect}
                  onSelectAll={handleSelectAll}
                  onUserClick={handleUserClick}
                  sortConfig={sortConfig}
                  onSortChange={handleSortChange}
                  ruleSets={newProjectRuleSets}
                />
              )}
            </div>
          )}

          {!isLoading && (hasPrevPage || hasNextPage) && (
            // `TABLE_FOOTER` draws a top border for the table it normally closes; this bar stands
            // alone in a card of its own, where that line would double the card's own edge. The
            // child selector beats the utility on specificity, so the order of the two does not
            // decide which wins.
            <div className={`${CARD} overflow-hidden [&>div]:border-t-0`}>
              <div className={TABLE_FOOTER}>
                <div className="flex flex-1 items-center justify-center gap-2">
                  <ActionButton
                    variant="ghost"
                    iconOnly={true}
                    icon={<CaretLeft size={16} aria-hidden="true" />}
                    disabled={!hasPrevPage}
                    onClick={() => setPage(Math.max(1, page - 1))}
                    aria-label={t('common.previousPage')}
                  />
                  <span className="text-sm text-gray-600">
                    {totalPages != null
                      ? `${t('common.page')} ${page} ${t('common.of')} ${totalPages}`
                      : `${t('common.page')} ${page}`}
                  </span>
                  <ActionButton
                    variant="ghost"
                    iconOnly={true}
                    icon={<CaretRight size={16} aria-hidden="true" />}
                    disabled={!hasNextPage}
                    onClick={() => setPage(page + 1)}
                    aria-label={t('common.nextPage')}
                  />
                </div>
                {totalCount != null && (
                  <span className="shrink-0 text-sm text-gray-600">{`${Math.min(page * pageSize, totalCount)} / ${totalCount}`}</span>
                )}
              </div>
            </div>
          )}
        </section>
      </Reveal>

      {/* Outside the reveals on purpose: it is `position: fixed`, and a transformed ancestor
          would make the wrapper its containing block and pin it to the page instead. */}
      <UploadQueuePanel uploads={uploads} onCancel={cancelUpload} />

      <ConfirmDeleteDialog
        visible={showDeleteDialog}
        title={t('home.deleteTitle')}
        message={t('home.deleteConfirm', { count: selectedProjectIds.size })}
        isDeleting={isDeletingProjects}
        onConfirm={handleDeleteSelected}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </div>
  );
};
